import type { Animation, Keyframe, EasingFunction } from '../types';

export function applyEasing(t: number, easing: EasingFunction = 'linear'): number {
  switch (easing) {
    case 'linear': return t;
    case 'easeIn': return t * t;
    case 'easeOut': return t * (2 - t);
    case 'easeInOut': return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    default: return t;
  }
}

export function calculateAnimationValue(animation: Animation, playbackPosition: number): number | null {
  const { keyframes, trigger, loop } = animation;
  if (keyframes.length === 0) return null;

  const triggerTime = trigger?.time ?? 0;
  const elapsed = playbackPosition - triggerTime;

  if (elapsed < 0) return keyframes[0].value;

  const duration = keyframes[keyframes.length - 1].at;
  let localElapsed = elapsed;
  if (loop && duration > 0 && elapsed > duration) {
    localElapsed = elapsed % duration;
  }
  if (!loop && elapsed >= duration) {
    return keyframes[keyframes.length - 1].value;
  }

  let startFrame: Keyframe = keyframes[0];
  let endFrame: Keyframe = keyframes[0];
  for (let i = 0; i < keyframes.length - 1; i++) {
    if (localElapsed >= keyframes[i].at && localElapsed <= keyframes[i + 1].at) {
      startFrame = keyframes[i];
      endFrame = keyframes[i + 1];
      break;
    }
  }

  if (localElapsed >= keyframes[keyframes.length - 1].at) {
    return keyframes[keyframes.length - 1].value;
  }

  const frameDuration = endFrame.at - startFrame.at;
  if (frameDuration === 0) return endFrame.value;

  const frameProgress = (localElapsed - startFrame.at) / frameDuration;
  const easedProgress = applyEasing(frameProgress, endFrame.easing);
  return startFrame.value + (endFrame.value - startFrame.value) * easedProgress;
}

export function calculateAnimatedStyles(
  animations: Animation[] | undefined,
  playbackPosition: number
): Record<string, number> {
  if (!animations || animations.length === 0) return {};
  const styles: Record<string, number> = {};
  for (const anim of animations) {
    const value = calculateAnimationValue(anim, playbackPosition);
    if (value !== null) styles[anim.property] = value;
  }
  return styles;
}
